import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { fmtUntil } from '../timefmt';

const CODES = {
  NETSUITE_NOT_CONNECTED: 'Connect your NetSuite account before pushing — everything the app writes has to run as you. The connect box is on screen now.',
  NETSUITE_RECONNECT_REQUIRED: 'Your NetSuite sign-in expired — reconnect (one click) and push again. The reconnect box is on screen now.',
};

// Turns a backend NetSuite-auth error into a friendly message and re-opens the
// connect prompt. Returns null for any other error.
export function nsConnectMessage(e) {
  const msg = CODES[e?.response?.data?.detail];
  if (msg) window.dispatchEvent(new Event('ns-oauth-prompt'));
  return msg || null;
}

// Per-user NetSuite sign-in, required for every role except US datasheet
// creators (App.js decides who mounts this). Everything the app reads and
// writes runs as the connected user, so NetSuite's own System Information
// names them. There is no dismiss — the app is unusable until they connect.
export default function NetSuiteConnect({ toast }) {
  const [state, setState] = useState(null); // {enabled, status, expires_at}
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [fails, setFails] = useState(0);

  const load = useCallback(async () => {
    try {
      setState(await api.nsOauthStatus());
      setFails(0);
    } catch (e) {
      // Never assume "connected" when the check itself fails — retry, and after
      // a few attempts prompt anyway rather than silently skipping the sign-in.
      setFails(n => n + 1);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const flag = params.get('netsuite');
    if (flag) {
      params.delete('netsuite');
      window.history.replaceState({}, '', window.location.pathname + (params.toString() ? '?' + params : ''));
      if (flag === 'connected') toast('NetSuite connected — your work is now recorded under your name in NetSuite');
      if (flag === 'error') toast('NetSuite did not complete the connection — try again, or ask your admin to check your role permissions.', true);
    }
    load();
  }, [load, toast]);

  // Retry a failed status check until it answers.
  useEffect(() => {
    if (!fails) return;
    const t = setTimeout(load, 10000);
    return () => clearTimeout(t);
  }, [fails, load]);

  useEffect(() => {
    window.addEventListener('ns-oauth-prompt', load);
    return () => window.removeEventListener('ns-oauth-prompt', load);
  }, [load]);

  // Re-check every half hour so a session lapsing mid-day still warns.
  useEffect(() => {
    const t = setInterval(load, 30 * 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  async function connect() {
    setBusy(true);
    try {
      const { url } = await api.nsOauthStart();
      window.location.assign(url);
    } catch (e) {
      setBusy(false);
      toast(e?.response?.data?.detail || 'Could not start the NetSuite sign-in', true);
    }
  }

  // Two failed checks in a row: prompt rather than let the sign-in be skipped.
  const unknown = fails >= 2 && !state;
  if (!unknown && (!state || !state.enabled)) return null;

  // Connected but about to lapse: warn without blocking, so nobody discovers
  // an expired NetSuite session halfway through a quote.
  if (state && state.status === 'connected') {
    if (!state.expiring_soon || dismissed) return null;
    return (
      <div data-testid="ns-expiry-banner" style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 60,
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
        padding: '10px 16px', background: '#7c2d12', color: '#fff',
        boxShadow: '0 -6px 18px rgba(0,0,0,.18)', fontSize: 13,
      }}>
        <strong style={{ letterSpacing: '.01em' }}>NetSuite session ends {fmtUntil(state.expires_at)}</strong>
        <span style={{ opacity: .85 }}>Reconnect now so a quote push doesn't fail partway through.</span>
        <button className="btn" data-testid="ns-expiry-reconnect-btn" disabled={busy}
          style={{ marginLeft: 'auto', background: '#fff', color: '#7c2d12', fontWeight: 700 }} onClick={connect}>
          {busy ? 'Opening NetSuite…' : 'Reconnect now'}
        </button>
        <button className="btn" data-testid="ns-expiry-dismiss-btn"
          style={{ background: 'transparent', color: '#fff', borderColor: 'rgba(255,255,255,.5)' }}
          onClick={() => setDismissed(true)}>Not now</button>
      </div>
    );
  }

  const reconnect = state && state.status === 'reconnect_required';

  return (
    <div className="modal-overlay" data-testid="ns-connect-modal">
      <div className="modal" style={{ maxWidth: 520 }}>
        <h3 style={{ margin: '0 0 6px', color: 'var(--brand-navy)' }} data-testid="ns-connect-title">
          {reconnect ? 'Reconnect your NetSuite account' : 'Connect your NetSuite account'}
        </h3>
        <p style={{ fontSize: 13, lineHeight: 1.6 }}>
          {reconnect
            ? 'Your NetSuite sign-in has expired (NetSuite requires it weekly). Reconnect so quotes and item changes keep showing your name in NetSuite — it takes one click if you are already signed in there.'
            : 'Sign in to NetSuite to continue. Everything this app does on your behalf — quotes, item creation, vendor prices — is then recorded under your name in NetSuite, not the integration account.'}
        </p>
        <p className="field-hint" data-testid="ns-connect-hint">
          {unknown
            ? 'We could not confirm your NetSuite connection just now. Sign in to be sure — it is one click if you are already signed in to NetSuite.'
            : 'You stay connected for a week at a time; the app refreshes your session in the background. NetSuite sign-in is required to use the app.'}
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button className="btn btn-primary" data-testid="ns-connect-btn" disabled={busy} onClick={connect}>
            {busy ? 'Opening NetSuite…' : (reconnect ? 'Reconnect NetSuite' : 'Connect NetSuite')}
          </button>
        </div>
      </div>
    </div>
  );
}
